import { type StyleDeclarationValue, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { THEME } from '../../../web/components-v2/Theme/theme';

export enum SupportNotificationType {
	Mention = 'Mention',
	Reply = 'Reply',
	Assignment = 'Assignment',
	Note = 'Note',
	Reassignment = 'Reassignment',
	Activity = 'Activity',
}

// The API omits a notification type, so keep these checks aligned with SupportNotificationPresentation.BaseTitle.
export function deriveSupportNotificationType(title: string): SupportNotificationType {
	const text = title.toLowerCase();
	// Match by shape first: an account named "Assigned Risk Specialists" would trip the loose word checks below.
	if (/ created new ticket( for .+?)? #/.test(text)) return SupportNotificationType.Activity;
	if (text.includes('mentioned you')) return SupportNotificationType.Mention;
	// Check "unassigned" first because it contains "assigned".
	if (text.includes('unassigned')) return SupportNotificationType.Reassignment;
	if (text.includes('assigned')) return SupportNotificationType.Assignment;
	if (text.includes('added a note')) return SupportNotificationType.Note;
	if (text.includes('replied') || text.includes('new reply')) return SupportNotificationType.Reply;
	return SupportNotificationType.Activity;
}

// API titles omit an actor field and may include urgency prefixes.
// Keep these patterns aligned with SupportNotificationPresentation.BaseTitle.
export function deriveSupportNotificationActorName(title: string): string | undefined {
	const text = title.replace(/^\[(?:Urgent|High)\] /, '');
	const customerReply = text.match(/^New reply from (.+?) on #/);
	if (customerReply) return customerReply[1];
	const outboundTicket = text.match(/^(.+?) created new ticket for .+? #/);
	if (outboundTicket) return outboundTicket[1];
	const newTicket = text.match(/^(.+?) created new ticket #/);
	if (newTicket) return newTicket[1];
	const agentAction = text.match(/^(.+?) (?:mentioned you|replied|added a note|unassigned|assigned)\b/);
	return agentAction ? agentAction[1] : undefined;
}

type GlyphProps = { size?: number };

const strokeGlyph =
	(path: React.ReactNode, strokeWidth = 2.2): React.FC<GlyphProps> =>
	({ size = 10 }) => (
		<svg
			width={size}
			height={size}
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth={strokeWidth}
			strokeLinecap='round'
			strokeLinejoin='round'
			aria-hidden='true'
		>
			{path}
		</svg>
	);

const MentionGlyph = strokeGlyph(
	<>
		<circle cx='12' cy='12' r='4' />
		<path d='M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94' />
	</>,
	2.4
);

const ReplyGlyph = strokeGlyph(
	<>
		<polyline points='9 17 4 12 9 7' />
		<path d='M20 18v-2a4 4 0 0 0-4-4H4' />
	</>,
	2.4
);

const AssignmentGlyph = strokeGlyph(
	<>
		<path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
		<circle cx='9' cy='7' r='4' />
		<line x1='19' y1='8' x2='19' y2='14' />
		<line x1='22' y1='11' x2='16' y2='11' />
	</>
);

const NoteGlyph = strokeGlyph(
	<>
		<path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
		<path d='M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z' />
	</>,
	2.4
);

const ReassignmentGlyph = strokeGlyph(
	<>
		<polyline points='17 1 21 5 17 9' />
		<path d='M3 11V9a4 4 0 0 1 4-4h14' />
		<polyline points='7 23 3 19 7 15' />
		<path d='M21 13v2a4 4 0 0 1-4 4H3' />
	</>,
	2.4
);

const ActivityGlyph = strokeGlyph(
	<>
		<path d='M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9' />
		<path d='M13.73 21a2 2 0 0 1-3.46 0' />
	</>,
	1.8
);

const accentStyles = StyleSheet.create({
	mention: { backgroundColor: THEME.colors.primary.main },
	reply: { backgroundColor: THEME.colors.pill.teal.icon },
	assignment: { backgroundColor: THEME.colors.secondary.main },
	note: { backgroundColor: THEME.colors.pill.teal.icon },
	reassignment: { backgroundColor: THEME.colors.icon.gray },
	activity: { backgroundColor: THEME.colors.icon.gray },
});

interface SupportNotificationDisplay {
	accent: StyleDeclarationValue;
	Icon: React.FC<GlyphProps>;
	label: string;
}

const DISPLAY: Record<SupportNotificationType, SupportNotificationDisplay> = {
	[SupportNotificationType.Mention]: { accent: accentStyles.mention, Icon: MentionGlyph, label: 'Mention' },
	[SupportNotificationType.Reply]: { accent: accentStyles.reply, Icon: ReplyGlyph, label: 'Reply' },
	[SupportNotificationType.Assignment]: { accent: accentStyles.assignment, Icon: AssignmentGlyph, label: 'Assignment' },
	[SupportNotificationType.Note]: { accent: accentStyles.note, Icon: NoteGlyph, label: 'Note' },
	[SupportNotificationType.Reassignment]: {
		accent: accentStyles.reassignment,
		Icon: ReassignmentGlyph,
		label: 'Reassignment',
	},
	[SupportNotificationType.Activity]: { accent: accentStyles.activity, Icon: ActivityGlyph, label: 'Activity' },
};

export const getSupportNotificationDisplay = (type: SupportNotificationType): SupportNotificationDisplay =>
	DISPLAY[type];
